import { type AxialCoord, hexKey } from "../hex";
import { generateMap, MAP_CENTRE } from "../map";
import { seedRng } from "../rng";
import { symmetryOrder } from "../symmetry";
import type { CourtRules, RunOptions, RunRules } from "../types/rules";
import {
  FACTION_IDS,
  type FactionId,
  type FactionTable,
  type HexOwner,
  type King,
  type RunState,
  type StructureKind,
  type Terrain,
} from "../types/state";
import { pickSymmetricStarts, placeSymmetricLand } from "./orbitPlacement";
import { pickStarts, placeLand } from "./placement";
import { newSeats, spawnSeat } from "./seats";
import { sidesRefusal } from "./sides";

/**
 * A new run from a seed: the map, the land placed on it (`placement.ts`), the hexes shifted so the
 * player starts at the origin, the pools, the Kings and the seats, each soldier seat's unit spawned.
 */

const ORIGIN: AxialCoord = { q: 0, r: 0 };

const DUEL_FACTION_COUNT = 2;

/**
 * Both factions start on the map's edge, on twin hexes through the procgen centre (Increment
 * 3.18). Land is placed in map coordinates, where the mirror is the centre, and everything is
 * then shifted so the player's start is the origin, which keeps every caller's "origin =
 * player start" assumption true. Both pools hold `startSupply`; no unit exists yet. With
 * `rules.starts` (K5) the map, the starts and the land come in a symmetry group's orbits instead;
 * with `rules.sides` too (K6) the kingdoms are placed side by side in the order of the sides, so
 * side j's kingdoms are the group's j-th images of side 1's.
 */
export function createRun(seed: number, options: RunOptions): RunState {
  const factionCount = options.factionCount ?? DUEL_FACTION_COUNT;
  if (
    !Number.isInteger(factionCount) ||
    factionCount < DUEL_FACTION_COUNT ||
    factionCount > FACTION_IDS.length
  ) {
    throw new RangeError(`a run holds 2 to ${FACTION_IDS.length} kingdoms, not ${factionCount}`);
  }
  const factions = FACTION_IDS.slice(0, factionCount);
  const symmetric = options.rules.starts;
  const placed = placementOrder(factions, options.rules);
  const map = generateMap(seedRng(seed), options.map, symmetric?.symmetry);
  const starts = symmetric
    ? pickSymmetricStarts(map.hexes, map.rng, options.rules, factionCount, symmetric)
    : pickStarts(map.hexes, map.rng, options.rules, factionCount);
  const land = symmetric
    ? placeSymmetricLand(
        map.hexes,
        starts,
        options.rules,
        options.map.noiseScale,
        symmetric.symmetry,
      )
    : placeLand(map.hexes, starts, options.rules, options.map.noiseScale);
  const home = starts.seats[placed.indexOf("player")] ?? MAP_CENTRE;
  const shift = (coord: AxialCoord): AxialCoord => ({ q: coord.q - home.q, r: coord.r - home.r });
  const capitals = starts.seats.map(shift);
  const capitalOf = (faction: FactionId): AxialCoord | undefined =>
    capitals[placed.indexOf(faction)];
  const ownerAt = new Map<string, FactionId>();
  for (const faction of factions) {
    const capital = capitalOf(faction);
    if (capital) {
      ownerAt.set(hexKey(capital), faction);
    }
  }
  const perFaction = <Value>(value: (capital: AxialCoord) => Value): FactionTable<Value> =>
    Object.fromEntries(
      factions.map((faction) => [faction, value(capitalOf(faction) ?? ORIGIN)]),
    ) as FactionTable<Value>;
  const { startSupply, court, soldier, finds } = options.rules;
  const seats = newSeats(factions, options);
  const run: RunState = {
    seed,
    tick: 0,
    rng: land.rng,
    status: "running",
    rules: options.rules,
    hexes: map.hexes.map((mapCoord) => {
      const key = hexKey(mapCoord);
      const coord = shift(mapCoord);
      const owner: HexOwner = ownerAt.get(hexKey(coord)) ?? "none";
      const terrain: Terrain = land.rough.has(key) ? "rough" : "open";
      // Both starts are cities: that is where all growth comes from.
      const structure = owner === "none" ? (land.structures.get(key) ?? "none") : "city";
      return {
        coord,
        owner,
        razed: false,
        terrain,
        structure,
        walls: initialWalls(options.rules, terrain, structure, owner),
        structureReadyTick: 0,
        unitBuild: "none" as const,
        unitBuildReadyTick: 0,
        mayor: false,
      };
    }),
    units: [],
    factions,
    supply: perFaction(() => startSupply),
    population: perFaction(() => options.rules.population?.start ?? 0),
    growth: perFaction(() => 0),
    nextUnitId: 1,
    enemyDespairTicks: 0,
    kings: court ? perFaction((capital) => crown(capital, court)) : null,
    seats,
    ...(finds ? { revealed: perFaction((): readonly string[] => []) } : {}),
    ...(finds ? { spent: [] } : {}),
    ...(finds && options.findsSalt !== undefined ? { findsSalt: options.findsSalt >>> 0 } : {}),
  };
  if (!soldier) {
    return run;
  }
  return seats.reduce(
    (spawned, seat) => (seat.role === "soldier" ? spawnSeat(spawned, seat.id, soldier) : spawned),
    run,
  );
}

/**
 * The order the starts are drawn for (K6): the kingdoms side by side as `rules.sides` lists them,
 * or the run's own order without sides. Sides that are not the run's, or that a symmetry group
 * cannot hold as images of one another, are refused.
 */
function placementOrder(factions: readonly FactionId[], rules: RunRules): readonly FactionId[] {
  const sides = rules.sides;
  if (!sides) {
    return factions;
  }
  const refusal = sidesRefusal(sides, factions);
  if (refusal !== undefined) {
    throw new RangeError(`these sides cannot play: ${refusal}`);
  }
  const symmetry = rules.starts?.symmetry;
  if (
    symmetry !== undefined &&
    (sides.length !== symmetryOrder(symmetry) ||
      sides.some((side) => side.length !== sides[0]?.length))
  ) {
    throw new RangeError(`${sides.length} sides of unequal or wrong size for ${symmetry}`);
  }
  return sides.flat();
}

/**
 * A hex's walls at the start of a run: rough ground's, else a city's (a neutral one's own), else
 * none. Not `wallMax`, which takes the largest: on a rough hex that holds a neutral city the rough
 * ground's walls stand here even when the city's would be higher.
 */
function initialWalls(
  rules: RunRules,
  terrain: Terrain,
  structure: StructureKind,
  owner: HexOwner,
): number {
  if (terrain === "rough") {
    return rules.roughWalls;
  }
  if (structure !== "city") {
    return 0;
  }
  return owner === "none" ? rules.neutralCityWalls : rules.cityWalls;
}

/** A King at the start of a run: on its capital, Will full at the starting Trust. */
function crown(capital: AxialCoord, court: CourtRules): King {
  return {
    coord: capital,
    movedTick: 0,
    will: Math.min(court.trustMax, court.trustBase + court.trustCapital),
    flights: 0,
    capital,
  };
}
